import { Loader } from "@mantine/core";
import type React from "react";
import { useEffect, useState } from "react";
import apiService from "../../services/api";
import type { TrainerPortalProps } from "../../types";
import PortalLayout, { type PortalTab } from "../shared/PortalLayout";
import BankAccountTab, { type ConnectStatusExtended } from "./tabs/BankAccountTab";
import TrainerHistoryTab from "./tabs/TrainerHistoryTab";
import TrainerProfileTab, { type TrainerEditFormData } from "./tabs/TrainerProfileTab";
import TrainerRequestsTab from "./tabs/TrainerRequestsTab";
import TrainerEarningsDashboard from "./TrainerEarningsDashboard";

interface TrainerRequest {
  id: string;
  status: string;
  client_username?: string;
  client_name?: string;
  pet_name?: string;
  pet_breed?: string;
  pet_age?: string;
  explanation?: string;
  duration_minutes?: number;
  total_price?: number;
  requested_at: string;
  accepted_at?: string;
  payment_status?: string;
  [key: string]: unknown;
}

interface DeliverablesFormData {
  summary?: string;
  checklist_items?: string[];
  [key: string]: unknown;
}

function TrainerPortal({
  onBackToHome,
  onLogout,
  isLoggedIn,
}: TrainerPortalProps): React.ReactElement {

  const [activeTab, setActiveTab] = useState<string>("profile");
  const [trainerProfile, setTrainerProfile] = useState<Record<string, unknown> | null>(null);
  const [sessionHistory, setSessionHistory] = useState<TrainerRequest[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [editingProfile, setEditingProfile] = useState<boolean>(false);
  const [editFormData, setTrainerEditFormData] = useState<TrainerEditFormData>({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    address: "",
    business_name: "",
    time_zone: "",
    business_street: "",
    business_apt: "",
    business_city: "",
    business_state: "",
    business_zip: "",
    business_address: "",
    credentials: [],
    training_philosophy: [],
    areas_of_expertise: [],
    experience_years: "",
    year_began_training: "",
    first_10_min_rate: "",
    per_minute_rate: "",
    bio: "",
    legal_states: [],
    current_rating: 0.0,
  });
  const [acceptingRequests, setAcceptingRequests] = useState<boolean>(false);
  const [toggleLoading, setToggleLoading] = useState<boolean>(false);
  const [trainerRequests, setTrainerRequests] = useState<TrainerRequest[]>([]);
  const [requestsLoading, setRequestsLoading] = useState<boolean>(false);
  const [deliverablesData, setDeliverablesData] = useState<Record<string, DeliverablesFormData>>(
    {},
  );
  const [savingDeliverables, setSavingDeliverables] = useState<Record<string, boolean>>({});
  const [uploadingPhoto, setUploadingPhoto] = useState<boolean>(false);
  const [selectedDeliverablesRequest, setSelectedDeliverablesRequest] =
    useState<TrainerRequest | null>(null);
  const [connectStatus, setConnectStatus] = useState<ConnectStatusExtended | null>(null);
  const [connectLoading, setConnectLoading] = useState<boolean>(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  // Pending-approval trainers can only use the profile tab. If the portal opens
  // on another tab (e.g. from a bookmark), snap them back to profile.
  useEffect(() => {
    if (trainerProfile?.application_status === "pending" && activeTab !== "profile") {
      setActiveTab("profile");
    }
  }, [trainerProfile, activeTab]);

  const loadInitialData = async (): Promise<void> => {
    await Promise.all([
      loadTrainerProfile(),
      loadActiveTrainingRequests(),
      loadSessionHistory(),
      loadTrainerAvailability(),
    ]);
  };

  const loadTrainerAvailability = async (): Promise<void> => {
    const trainerId = localStorage.getItem("trainerId");
    if (!trainerId) return;
    try {
      const response = (await apiService.getTrainerAvailability(trainerId)) as Record<
        string,
        unknown
      >;
      if (response.Code === 200) {
        const isActive = response.is_active as boolean;
        setAcceptingRequests(isActive);
        if (isActive) {
          await loadTrainerRequests();
        }
      }
    } catch (err) {
      console.error("Error loading trainer availability:", err);
    }
  };

  const loadTrainerRequests = async (): Promise<void> => {
    try {
      setRequestsLoading(true);
      const trainerId = localStorage.getItem("trainerId");

      if (trainerId) {
        const response = (await apiService.getTrainerRequests(trainerId)) as Record<
          string,
          unknown
        >;

        if (response.Code === 200) {
          const requests = (response.requests as TrainerRequest[]) || [];
          setTrainerRequests(requests);

          for (const request of requests) {
            if (request.status === "completed") {
              try {
                const deliverablesResponse = (await apiService.getDeliverables(
                  request.id,
                )) as Record<string, unknown>;
                if (deliverablesResponse.Code === 200 && deliverablesResponse.data) {
                  setDeliverablesData((prev) => ({
                    ...prev,
                    [request.id]: deliverablesResponse.data as DeliverablesFormData,
                  }));
                }
              } catch (_err) {
                // Deliverables not yet created for this request
              }
            }
          }
        } else {
          console.error("Failed to load trainer requests:", response.Message);
          setError(`Failed to load trainer requests: ${response.Message}`);
        }
      } else {
        console.error("No trainer ID found in localStorage");
        setError("No trainer ID found. Please complete your profile setup.");
      }
    } catch (err) {
      console.error("Error loading trainer requests:", err);
      setError("Failed to load trainer requests");
    } finally {
      setRequestsLoading(false);
    }
  };

  const handleSaveDeliverables = async (
    requestId: string,
    _request: TrainerRequest,
  ): Promise<void> => {
    try {
      setSavingDeliverables((prev) => ({ ...prev, [requestId]: true }));
      const trainerId = localStorage.getItem("trainerId");
      const formData = deliverablesData[requestId] || {};

      const checklistItems = (formData.checklist_items || [])
        .filter((item: string) => item && item.trim().length > 0)
        .map((item: string) => item.trim());

      const response = (await apiService.createOrUpdateDeliverables({
        session_request_id: requestId,
        trainer_id: trainerId,
        summary: formData.summary || "",
        checklist_items: checklistItems,
      })) as Record<string, unknown>;

      if (response.Code === 200) {
        const deliverablesResponse = (await apiService.getDeliverables(requestId)) as Record<
          string,
          unknown
        >;
        if (deliverablesResponse.Code === 200 && deliverablesResponse.data) {
          setDeliverablesData((prev) => ({
            ...prev,
            [requestId]: deliverablesResponse.data as DeliverablesFormData,
          }));
        }
        alert("Deliverables saved successfully!");
      } else {
        alert(`Failed to save deliverables: ${response.Message}`);
      }
    } catch (err) {
      console.error("Error saving deliverables:", err);
      alert("Failed to save deliverables");
    } finally {
      setSavingDeliverables((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  const handleDeliverablesInputChange = (
    requestId: string,
    field: string,
    value: string | string[],
  ): void => {
    setDeliverablesData((prev) => ({
      ...prev,
      [requestId]: {
        ...prev[requestId],
        [field]: value,
      },
    }));
  };

  const handleAddChecklistItem = (requestId: string): void => {
    setDeliverablesData((prev) => ({
      ...prev,
      [requestId]: {
        ...prev[requestId],
        checklist_items: [...(prev[requestId]?.checklist_items || []), ""],
      },
    }));
  };

  const handleRemoveChecklistItem = (requestId: string, index: number): void => {
    setDeliverablesData((prev) => {
      const items = [...(prev[requestId]?.checklist_items || [])];
      items.splice(index, 1);
      return {
        ...prev,
        [requestId]: {
          ...prev[requestId],
          checklist_items: items,
        },
      };
    });
  };

  const handleChecklistItemChange = (requestId: string, index: number, value: string): void => {
    setDeliverablesData((prev) => {
      const items = [...(prev[requestId]?.checklist_items || [])];
      items[index] = value;
      return {
        ...prev,
        [requestId]: {
          ...prev[requestId],
          checklist_items: items,
        },
      };
    });
  };

  const handleOpenDeliverables = async (request: TrainerRequest): Promise<void> => {
    setSelectedDeliverablesRequest(request);
    if (deliverablesData[request.id]) return;
    try {
      const deliverablesResponse = (await apiService.getDeliverables(request.id)) as Record<
        string,
        unknown
      >;
      if (deliverablesResponse.Code === 200 && deliverablesResponse.data) {
        setDeliverablesData((prev) => ({
          ...prev,
          [request.id]: deliverablesResponse.data as DeliverablesFormData,
        }));
      } else {
        setDeliverablesData((prev) => ({
          ...prev,
          [request.id]: { summary: "", checklist_items: [] },
        }));
      }
    } catch (err) {
      console.error("Error loading deliverables:", err);
      setDeliverablesData((prev) => ({
        ...prev,
        [request.id]: { summary: "", checklist_items: [] },
      }));
    }
  };

  const loadTrainerProfile = async (): Promise<void> => {
    try {
      setLoading(true);
      const trainerId = localStorage.getItem("trainerId");

      if (trainerId) {
        const response = (await apiService.getTrainerProfile(trainerId)) as Record<string, unknown>;

        if (response.Code === 200) {
          const data = response.data as Record<string, unknown>;
          setTrainerProfile(data);

          const parseBusinessAddress = (
            address: string | undefined,
          ): {
            street: string;
            apt: string;
            city: string;
            state: string;
            zip: string;
          } => {
            if (!address) return { street: "", apt: "", city: "", state: "", zip: "" };
            const parts = address.split(",").map((p: string) => p.trim());
            if (parts.length >= 3) {
              return {
                street: parts[0] || "",
                apt:
                  parts[1]?.includes("Apt") ||
                  parts[1]?.includes("Ste") ||
                  parts[1]?.includes("Unit")
                    ? parts[1]
                    : "",
                city: parts[parts.length - 2] || "",
                state: parts[parts.length - 1]?.match(/^([A-Z]{2})\s*(\d{5})$/)?.[1] || "",
                zip: parts[parts.length - 1]?.match(/^([A-Z]{2})\s*(\d{5})$/)?.[2] || "",
              };
            }
            return { street: "", apt: "", city: "", state: "", zip: "" };
          };

          const businessAddressParts = parseBusinessAddress(
            data.business_address as string | undefined,
          );

          const newTrainerEditFormData: TrainerEditFormData = {
            first_name: (data.first_name as string) || "",
            last_name: (data.last_name as string) || "",
            email: (data.email as string) || "",
            phone_number: (data.phone_number as string) || "",
            address: (data.address as string) || "",
            business_name: (data.business_name as string) || "",
            time_zone: (data.time_zone as string) || "",
            business_street: businessAddressParts.street,
            business_apt: businessAddressParts.apt,
            business_city: businessAddressParts.city,
            business_state: businessAddressParts.state,
            business_zip: businessAddressParts.zip,
            business_address: (data.business_address as string) || "",
            credentials: Array.isArray(data.credentials)
              ? (data.credentials as string[])
              : data.credentials
                ? (data.credentials as string).split(",").map((c: string) => c.trim())
                : [],
            training_philosophy: Array.isArray(data.training_philosophy)
              ? (data.training_philosophy as string[])
              : data.training_philosophy
                ? (data.training_philosophy as string).split(",").map((p: string) => p.trim())
                : [],
            areas_of_expertise: Array.isArray(data.areas_of_expertise)
              ? (data.areas_of_expertise as string[])
              : data.areas_of_expertise
                ? (data.areas_of_expertise as string).split(",").map((a: string) => a.trim())
                : [],
            experience_years: (data.experience_years as string) || "",
            year_began_training: (data.year_began_training as string) || "",
            first_10_min_rate: (data.first_10_min_rate as string) || "",
            per_minute_rate: (data.per_minute_rate as string) || "",
            bio: (data.bio as string) || "",
            legal_states: Array.isArray(data.legal_states)
              ? (data.legal_states as string[])
              : data.legal_states
                ? (data.legal_states as string).split(",").map((s: string) => s.trim())
                : [],
            current_rating: (data.current_rating as number) || 0.0,
          };

          setTrainerEditFormData(newTrainerEditFormData);
        } else {
          console.error("Trainer profile not found:", response.Message);
          setError(`Failed to load trainer profile: ${response.Message}`);
        }
      } else {
        console.error("No trainer ID found in localStorage");
        setError("No trainer ID found. Please complete your profile setup.");
      }
    } catch (err) {
      console.error("Error loading trainer profile:", err);
      setError("Failed to load trainer profile");
    } finally {
      setLoading(false);
    }
  };

  const loadActiveTrainingRequests = async (): Promise<void> => {
    try {
      await loadTrainerRequests();
    } catch (err) {
      console.error("Error loading active training requests:", err);
    }
  };

  const loadSessionHistory = async (): Promise<void> => {
    try {
      const trainerId = localStorage.getItem("trainerId");
      if (trainerId) {
        const response = (await apiService.getTrainerRequests(trainerId)) as Record<
          string,
          unknown
        >;
        if (response.Code === 200) {
          const allRequests = (response.requests as TrainerRequest[]) || [];
          const completedRequests = allRequests.filter(
            (req: TrainerRequest) => req.status === "completed",
          );
          setSessionHistory(completedRequests);

          for (const request of completedRequests) {
            try {
              const deliverablesResponse = (await apiService.getDeliverables(request.id)) as Record<
                string,
                unknown
              >;
              if (deliverablesResponse.Code === 200 && deliverablesResponse.data) {
                setDeliverablesData((prev) => ({
                  ...prev,
                  [request.id]: deliverablesResponse.data as DeliverablesFormData,
                }));
              }
            } catch (_err) {
              // Deliverables not yet created for this session
            }
          }
        } else {
          setSessionHistory([]);
        }
      }
    } catch (err) {
      console.error("Error loading session history:", err);
      setSessionHistory([]);
    }
  };

  const handleTabChange = (tab: string): void => {
    setActiveTab(tab);
    if (tab === "requests") {
      loadActiveTrainingRequests();
    } else if (tab === "history") {
      loadSessionHistory();
    } else if (tab === "bank") {
      loadConnectStatus();
    }
  };

  const loadConnectStatus = async (): Promise<void> => {
    const trainerId = localStorage.getItem("trainerId");
    if (!trainerId) return;
    try {
      setConnectLoading(true);
      const response = (await apiService.getConnectAccountStatus(
        trainerId,
      )) as ConnectStatusExtended;
      if (response.Code === 200) {
        setConnectStatus(response);
      }
    } catch (err) {
      console.error("Error loading connect status:", err);
    } finally {
      setConnectLoading(false);
    }
  };

  const handleConnectBankAccount = async (): Promise<void> => {
    const trainerId = localStorage.getItem("trainerId");
    if (!trainerId) return;
    try {
      setConnectLoading(true);
      if (!connectStatus?.has_connect_account) {
        const createResponse = (await apiService.createConnectAccount(trainerId)) as Record<
          string,
          unknown
        >;
        if (createResponse.Code !== 200) {
          alert("Failed to create Connect account: " + createResponse.Message);
          return;
        }
      }
      const linkResponse = (await apiService.createOnboardingLink(trainerId)) as Record<
        string,
        unknown
      >;
      if (linkResponse.Code === 200 && linkResponse.url) {
        window.location.href = linkResponse.url as string;
      } else {
        alert("Failed to create onboarding link: " + linkResponse.Message);
      }
    } catch (err) {
      console.error("Error starting Connect onboarding:", err);
      alert("Something went wrong. Please try again.");
    } finally {
      setConnectLoading(false);
    }
  };

  const handleEditProfile = (): void => {
    setEditingProfile(true);
  };

  const handleSaveProfile = async (): Promise<void> => {
    try {
      setLoading(true);
      const trainerId = localStorage.getItem("trainerId");

      let fullBusinessAddress = editFormData.business_street;
      if (editFormData.business_apt) {
        fullBusinessAddress += ", " + editFormData.business_apt;
      }
      if (editFormData.business_city) {
        fullBusinessAddress += ", " + editFormData.business_city;
      }
      if (editFormData.business_state) {
        fullBusinessAddress += ", " + editFormData.business_state;
      }
      if (editFormData.business_zip) {
        fullBusinessAddress += " " + editFormData.business_zip;
      }

      const updateData = {
        first_name: editFormData.first_name,
        last_name: editFormData.last_name,
        phone_number: editFormData.phone_number,
        business_name: editFormData.business_name,
        time_zone: editFormData.time_zone,
        business_address: fullBusinessAddress,
        credentials: editFormData.credentials,
        training_philosophy: editFormData.training_philosophy,
        areas_of_expertise: editFormData.areas_of_expertise,
        experience_years: editFormData.experience_years,
        year_began_training: editFormData.year_began_training,
        first_10_min_rate: parseFloat(parseFloat(editFormData.first_10_min_rate || "0").toFixed(2)),
        per_minute_rate: parseFloat(parseFloat(editFormData.per_minute_rate || "0").toFixed(2)),
        bio: editFormData.bio,
        legal_states: editFormData.legal_states,
      };

      const response = (await apiService.updateTrainerProfile(
        trainerId as string,
        updateData,
      )) as Record<string, unknown>;

      if (response.Code === 200) {
        setTrainerProfile({
          ...trainerProfile,
          ...editFormData,
        });
        setEditingProfile(false);
        setError(null);
      } else {
        setError((response.Message as string) || "Failed to update profile");
      }
    } catch (err) {
      console.error("Error updating profile:", err);
      setError("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Please select an image file");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError("Image size must be less than 10MB");
      return;
    }

    const trainerId = localStorage.getItem("trainerId");
    if (!trainerId) {
      setError("Trainer ID not found");
      return;
    }

    try {
      setUploadingPhoto(true);
      setError(null);

      const response = (await apiService.uploadTrainerProfilePhoto(trainerId, file)) as Record<
        string,
        unknown
      >;

      if (response.Code === 200) {
        await loadTrainerProfile();
        alert("Profile photo uploaded successfully!");
      } else {
        setError((response.Message as string) || "Failed to upload photo");
      }
    } catch (err: unknown) {
      console.error("Photo upload error:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to upload photo";
      setError(errorMessage);
    } finally {
      setUploadingPhoto(false);
      e.target.value = "";
    }
  };

  const handleCancelEdit = (): void => {
    setEditingProfile(false);

    if (trainerProfile) {
      const addressParts = trainerProfile.address
        ? (trainerProfile.address as string).split(", ")
        : ["", "", ""];
      const streetAddress = addressParts[0] || "";
      const stateZip = addressParts[1] || "";
      const stateZipParts = stateZip.split(" ");
      const state = stateZipParts[0] || "";
      const zipCode = stateZipParts[1] || "";

      setTrainerEditFormData({
        first_name: (trainerProfile.first_name as string) || "",
        last_name: (trainerProfile.last_name as string) || "",
        email: (trainerProfile.email as string) || "",
        phone_number: (trainerProfile.phone_number as string) || "",
        address: streetAddress,
        state: state,
        zip_code: zipCode,
        experience_years: (trainerProfile.experience_years as string) || "",
        hourly_rate: (trainerProfile.hourly_rate as string) || "",
        bio: (trainerProfile.bio as string) || "",
        business_name: (trainerProfile.business_name as string) || "",
        time_zone: (trainerProfile.time_zone as string) || "",
        business_address: (trainerProfile.business_address as string) || "",
        business_street: "",
        business_apt: "",
        business_city: "",
        business_state: "",
        business_zip: "",
        credentials: (trainerProfile.credentials as string[]) || [],
        training_philosophy: (trainerProfile.training_philosophy as string[]) || [],
        areas_of_expertise: (trainerProfile.areas_of_expertise as string[]) || [],
        year_began_training: (trainerProfile.year_began_training as string) || "",
        first_10_min_rate: (trainerProfile.first_10_min_rate as string) || "",
        per_minute_rate: (trainerProfile.per_minute_rate as string) || "",
        legal_states: (trainerProfile.legal_states as string[]) || [],
        current_rating: (trainerProfile.current_rating as number) || 0.0,
      });
    }
  };

  const handleInputChange = (field: string, value: string | string[]): void => {
    setTrainerEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCredentialToggle = (credential: string): void => {
    setTrainerEditFormData((prev) => ({
      ...prev,
      credentials: prev.credentials.includes(credential)
        ? prev.credentials.filter((c: string) => c !== credential)
        : [...prev.credentials, credential],
    }));
  };

  const handleTrainingPhilosophyToggle = (philosophy: string): void => {
    setTrainerEditFormData((prev) => ({
      ...prev,
      training_philosophy: prev.training_philosophy.includes(philosophy)
        ? prev.training_philosophy.filter((p: string) => p !== philosophy)
        : [...prev.training_philosophy, philosophy],
    }));
  };

  const handleAreaOfExpertiseToggle = (area: string): void => {
    setTrainerEditFormData((prev) => ({
      ...prev,
      areas_of_expertise: prev.areas_of_expertise.includes(area)
        ? prev.areas_of_expertise.filter((a: string) => a !== area)
        : [...prev.areas_of_expertise, area],
    }));
  };

  const handleAcceptSession = async (requestId: string): Promise<void> => {
    try {
      setLoading(true);
      const response = (await apiService.acceptSession(requestId)) as Record<string, unknown>;

      if (response.Code === 200) {
        await loadTrainerRequests();
        const statusMessage =
          response.status === "awaiting_payment"
            ? `Session accepted. Waiting for client payment of $${Number(response.total_price || 0).toFixed(2)}.`
            : "Session accepted. You can join the session when you are ready.";
        alert(statusMessage);
      } else {
        setError((response.Message as string) || "Failed to accept session");
      }
    } catch (err) {
      console.error("Error accepting session:", err);
      setError("Failed to accept session");
    } finally {
      setLoading(false);
    }
  };

  const handleDeclineSession = async (requestId: string): Promise<void> => {
    try {
      const response = (await apiService.declineSession(requestId)) as Record<string, unknown>;
      if (response.Code === 200) {
        await loadTrainerRequests();
      }
    } catch (err) {
      console.error("Error declining session:", err);
    }
  };

  const handleJoinSession = (requestId: string): void => {
    setLoading(true);
    (apiService.registerSessionJoin(requestId, "trainer") as Promise<Record<string, unknown>>)
      .then((response: Record<string, unknown>) => {
        if (response.Code === 200) {
          window.location.href = `/video-call?request_id=${requestId}`;
        } else {
          setError((response.Message as string) || "Failed to join session");
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        console.error("Error registering trainer join:", err);
        setError("Failed to join session");
        setLoading(false);
      });
  };

  const handleAcceptingRequestsToggle = async (): Promise<void> => {
    try {
      setToggleLoading(true);
      const trainerId = localStorage.getItem("trainerId");

      if (!trainerId) {
        setError("No trainer ID found");
        return;
      }

      if (acceptingRequests) {
        const response = (await apiService.makeTrainerInactive(trainerId)) as Record<
          string,
          unknown
        >;

        if (response.Code === 200) {
          setAcceptingRequests(false);
          setError(null);
        } else {
          setError((response.Message as string) || "Failed to make trainer inactive");
        }
      } else {
        const response = (await apiService.makeTrainerActive(trainerId)) as Record<string, unknown>;

        if (response.Code === 200) {
          setAcceptingRequests(true);
          setError(null);
          await loadTrainerRequests();
        } else {
          setError((response.Message as string) || "Failed to make trainer active");
        }
      }
    } catch (err) {
      console.error("Error toggling trainer status:", err);
      setError("Failed to update trainer status");
    } finally {
      setToggleLoading(false);
    }
  };


  if (loading && !trainerProfile) {
    return (
      <div className="font-['Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',sans-serif] bg-background min-h-screen text-foreground">
        <div className="bg-card text-foreground py-5 shadow-sm border-b border-border">
          <div className="max-w-[1200px] mx-auto px-3 sm:px-5 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-4 cursor-pointer">
              <img
                src="/images/logo.png"
                alt="Pheefee Logo"
                className="w-10 h-10 object-contain rounded-lg"
              />
              <span className="text-xl sm:text-2xl font-bold">Trainer Portal</span>
            </div>
          </div>
        </div>
        <div className="text-center py-[60px] px-5">
          <Loader color="blue" size="lg" className="mb-5" />
          <p className="text-muted-foreground text-[1.1rem]">Loading trainer profile...</p>
        </div>
      </div>
    );
  }

  const applicationStatus = (trainerProfile?.application_status as string | undefined) ?? null;
  const isPendingApproval = applicationStatus === "pending";
  const pendingReason = "Available once your application is approved.";

  const trainerTabs: PortalTab[] = [
    {
      key: "requests",
      label: "Active Requests",
      disabled: isPendingApproval,
      disabledReason: pendingReason,
    },
    {
      key: "history",
      label: "Session History",
      disabled: isPendingApproval,
      disabledReason: pendingReason,
    },
    { key: "profile", label: "My Profile" },
    {
      key: "earnings",
      label: "Earnings",
      disabled: isPendingApproval,
      disabledReason: pendingReason,
    },
    {
      key: "bank",
      label: "Bank Account",
      disabled: isPendingApproval,
      disabledReason: pendingReason,
    },
  ];


  return (
    <PortalLayout
      tabs={trainerTabs}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      onBackToHome={onBackToHome}
      onLogout={isLoggedIn ? onLogout : undefined}
      error={error}
      onDismissError={() => setError(null)}
    >
      {isPendingApproval && (
        <div className="bg-warning/10 border border-warning/30 rounded-xl px-5 py-4 mb-5 text-foreground">
          <div className="font-semibold text-warning mb-1">Application pending review</div>
          <div className="text-sm leading-relaxed text-muted-foreground">
            Your application has been submitted. Background checks are usually processed in
            2–5 business days. During this time we may also verify your credentials and
            insurance information. You can update your profile at any time; other tabs will
            unlock once your application is approved.
          </div>
        </div>
      )}

      <div className="bg-card rounded-[15px] p-4 sm:p-6 shadow-lg border border-border min-h-[600px]">
        {activeTab === "requests" && (
          <TrainerRequestsTab
            trainerRequests={trainerRequests}
            requestsLoading={requestsLoading}
            acceptingRequests={acceptingRequests}
            toggleLoading={toggleLoading}
            onRefresh={loadTrainerRequests}
            onAcceptingRequestsToggle={handleAcceptingRequestsToggle}
            onAcceptSession={handleAcceptSession}
            onDeclineSession={handleDeclineSession}
            onJoinSession={handleJoinSession}
          />
        )}
        {activeTab === "history" && (
          <TrainerHistoryTab
            sessionHistory={sessionHistory}
            loading={loading}
            onRefresh={loadSessionHistory}
            selectedDeliverablesRequest={selectedDeliverablesRequest}
            onOpenDeliverables={handleOpenDeliverables}
            onCloseDeliverables={() => setSelectedDeliverablesRequest(null)}
            deliverablesData={deliverablesData}
            savingDeliverables={savingDeliverables}
            onDeliverablesInputChange={handleDeliverablesInputChange}
            onChecklistItemChange={handleChecklistItemChange}
            onAddChecklistItem={handleAddChecklistItem}
            onRemoveChecklistItem={handleRemoveChecklistItem}
            onSaveDeliverables={handleSaveDeliverables}
          />
        )}
        {activeTab === "profile" && (
          <TrainerProfileTab
            trainerProfile={trainerProfile}
            editingProfile={editingProfile}
            editFormData={editFormData}
            uploadingPhoto={uploadingPhoto}
            loading={loading}
            onEditProfile={handleEditProfile}
            onCancelEdit={handleCancelEdit}
            onSaveProfile={handleSaveProfile}
            onPhotoUpload={handlePhotoUpload}
            onInputChange={handleInputChange}
            onCredentialToggle={handleCredentialToggle}
            onTrainingPhilosophyToggle={handleTrainingPhilosophyToggle}
            onAreaOfExpertiseToggle={handleAreaOfExpertiseToggle}
          />
        )}
        {activeTab === "earnings" && (
          <TrainerEarningsDashboard trainerId={localStorage.getItem("trainerId") || ""} />
        )}
        {activeTab === "bank" && (
          <BankAccountTab
            connectStatus={connectStatus}
            connectLoading={connectLoading}
            onConnectBankAccount={handleConnectBankAccount}
          />
        )}
      </div>
    </PortalLayout>
  );
}

export default TrainerPortal;
